/**
 * Seva Mandir V1 gate kit — the alignment map's V1 boards, value for value (colours, sizes,
 * radii, borders, icon size/stroke are copied from the boards' inline styles, 360×720 frame).
 * Every V1 gate screen is built from these pieces so the phone matches the map pixel for pixel.
 * Light only, like the boards. Hindi first; the English line sits under it.
 */
import {
  useContext,
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { V1_SPRITE, type V1IconName } from './sprite'
import { DemoDeviceError, downloadCaches, useSyncStatus } from '@/offline'
import { useNetworkStatus } from '@/api'
import { AuthContext } from '@/auth/context'
import { env } from '@/lib/env'
import { installAppUpdate, useAppUpdateWaiting, useSafeToUpdate } from '@/app/pwa/appUpdate'
import { refreshPhone, type RefreshResult } from '@/features/phone/refreshPhone'
import { C, speak } from './style'
import { tr, fmtWhen } from './text'

const FONT = "'Mukta', sans-serif"

// ---------------------------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------------------------

export function Icon({
  name,
  size = 24,
  sw = 2,
  style,
}: {
  name: V1IconName
  size?: number
  sw?: number
  style?: CSSProperties
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, ...style }}
    >
      <use href={`#ui-${name}`} />
    </svg>
  )
}

/**
 * The sprite, once per screen tree (the symbols are referenced by id), and the one font rule the
 * app's base styles would otherwise override (headings are Gelasio app-wide; the boards are Mukta).
 */
export function V1Sprite() {
  return (
    <>
      <style>{`[data-v1-screen] :is(h1, h2, h3, button, input) { font-family: ${FONT}; }`}</style>
      <div
        aria-hidden="true"
        style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
        dangerouslySetInnerHTML={{ __html: `<svg>${V1_SPRITE}</svg>` }}
      />
    </>
  )
}

// ---------------------------------------------------------------------------------------------
// Voice: the speaker buttons read the line aloud in Hindi (the phone's own voice, offline).
// ---------------------------------------------------------------------------------------------

export function SpeakButton({
  text,
  size = 48,
  label = tr('gate.v1.a11y.speakQuestion'),
}: {
  text: string
  size?: number
  label?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => speak(text)}
      style={{
        width: size,
        height: size,
        padding: 0,
        borderRadius: size / 2,
        border: 0,
        background: C.helpWash,
        color: C.help,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      <Icon name="speaker" size={24} />
    </button>
  )
}

// ---------------------------------------------------------------------------------------------
// Frame
// ---------------------------------------------------------------------------------------------

/**
 * Full-screen V1 frame over the phone shell (its header and tab bar sit at z-30): header,
 * optional band, a main column and a footer, like the 360×720 boards.
 */
export function V1Screen({
  header,
  band,
  children,
  footer,
  main,
  overlay,
  nav = false,
}: {
  /**
   * Leave the phone's bottom bar (Home · Sync · More) visible under the screen — the day-level
   * screens, so she can always leave the gate. A delivery in progress covers it (one task).
   */
  nav?: boolean
  header?: ReactNode
  band?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  /** Main column spacing; the boards use 10px 14px / gap 10 unless dense (weigh: 6px 12px / 5). */
  main?: { padding?: string; gap?: number }
  /** A sheet or dialog drawn over the screen. */
  overlay?: ReactNode
}) {
  return (
    <div
      data-v1-screen
      style={{
        position: 'fixed',
        inset: 0,
        // The phone bar is h-16 plus the safe area (PhoneLayout).
        bottom: nav ? 'calc(4rem + env(safe-area-inset-bottom, 0px))' : 0,
        zIndex: 40,
        background: C.bg,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <V1Sprite />
      <div
        data-v1-anim="screen"
        style={{
          width: '100%',
          maxWidth: 640,
          height: '100%',
          boxSizing: 'border-box',
          background: C.bg,
          color: C.ink,
          fontFamily: FONT,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          lineHeight: 1.35,
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        {(header || band) && (
          <header style={{ borderBottom: `1px solid ${C.rule}`, background: C.bg }}>
            {header}
            {band}
          </header>
        )}
        <main
          style={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: main?.padding ?? '10px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: main?.gap ?? 10,
          }}
        >
          {children}
        </main>
        {footer}
        {overlay}
      </div>
    </div>
  )
}

export function Footer({ children, dense = false }: { children: ReactNode; dense?: boolean }) {
  return (
    <footer
      style={{
        padding: dense ? '6px 12px 8px' : '8px 12px 10px',
        paddingBottom: `calc(${dense ? 8 : 10}px + env(safe-area-inset-bottom, 0px))`,
        display: 'flex',
        flexDirection: 'column',
        gap: dense ? 6 : 8,
        borderTop: `1px solid ${C.rule}`,
        background: C.bg,
      }}
    >
      {children}
    </footer>
  )
}

/** Fills the space between the content and what sits at the bottom of main (keypad). */
export const Grow = () => <div style={{ flexGrow: 1 }} />

// ---------------------------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------------------------

const roundBtn = (size: number): CSSProperties => ({
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: size / 2,
  border: `1.5px solid ${C.border}`,
  background: C.surface,
  color: C.ink,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  boxSizing: 'border-box',
  cursor: 'pointer',
})

export function Avatar({
  url,
  size = 40,
  radius,
  icon = 34,
  sw = 1.6,
  label = tr('gate.v1.a11y.farmerPhoto'),
}: {
  /** What the photo is of (the farmer by default). */
  label?: string
  url?: string | null
  size?: number
  radius?: number
  icon?: number
  sw?: number
}) {
  return (
    <span
      role="img"
      aria-label={label}
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius ?? size / 2,
        background: C.photo,
        color: C.photoInk,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {url ? (
        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Icon name="user" size={icon} sw={sw} />
      )}
    </span>
  )
}

export interface HeaderFarmer {
  name: string
  line: string
  photo?: string | null
  /** Screen-reader name of the photo (the farmer's by default; the operator's in the phone shell). */
  photoLabel?: string
}

/**
 * Top row: back, home (delivery screens), the farmer (photo, name, number · crop), records still
 * to send, मदद.
 * Second row: the step icon, the title in the language on screen, and the step dots.
 */
export function V1Header({
  onBack,
  onHome,
  farmer,
  pending,
  updatable = false,
  icon,
  title,
  step,
  onMore,
}: {
  onBack?: () => void
  /** Straight back to Home, leaving this farmer open where he is (several open at once). */
  onHome?: () => void
  farmer?: HeaderFarmer
  /** Records not yet sent; 0 shows the "all sent" ticks. */
  pending: number
  /**
   * The update chip may send, download and reload here: only screens where a reload loses nothing
   * (Home, the day board, Sync, More). Everywhere else it only shows the version.
   */
  updatable?: boolean
  /** The title row (icon and title); left out, the header is the top row only (phone shell). */
  icon?: V1IconName
  title?: string
  /** 1-based, of 6 (find → close). */
  step?: number
  /** ⋯ menu (Final check: cancel the purchase). */
  onMore?: () => void
}) {
  // The clock opens what is still to send; help lives in More.
  const [sheet, setSheet] = useState<null | 'sync'>(null)
  return (
    <>
      {sheet === 'sync' && <SyncSheet onClose={() => setSheet(null)} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 4px' }}>
        {onBack ? (
          <button
            type="button"
            aria-label={tr('gate.v1.back')}
            onClick={onBack}
            style={roundBtn(48)}
          >
            <Icon name="back" />
          </button>
        ) : null}
        {onHome ? (
          <button
            type="button"
            aria-label={tr('gate.v1.toHome')}
            title={tr('gate.v1.toHome')}
            onClick={onHome}
            style={roundBtn(48)}
          >
            <Icon name="home" />
          </button>
        ) : null}
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexGrow: 1, minWidth: 0 }}>
          {farmer && (
            <>
              <Avatar url={farmer.photo} label={farmer.photoLabel} />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <b
                  style={{
                    fontSize: 17,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {farmer.name}
                </b>
                <span style={{ fontSize: 14, color: C.muted, lineHeight: 1.2 }}>{farmer.line}</span>
              </span>
            </>
          )}
        </span>
        {pending > 0 ? (
          <button
            type="button"
            onClick={() => setSheet('sync')}
            aria-label={tr('gate.v1.a11y.pending', { count: pending })}
            title={tr('gate.v1.a11y.pending', { count: pending })}
            style={{
              cursor: 'pointer',
              height: 36,
              padding: '0 9px 0 6px',
              borderRadius: 18,
              background: C.surface,
              border: `1.5px solid ${C.border}`,
              color: C.ink,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <Icon name="clock" size={20} />
            {pending}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setSheet('sync')}
            aria-label={tr('gate.v1.a11y.allSent')}
            title={tr('gate.v1.a11y.allSent')}
            style={{
              border: 0,
              padding: 0,
              cursor: 'pointer',
              width: 36,
              height: 36,
              borderRadius: 18,
              background: C.okWash,
              color: C.ok,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="ticks" size={22} sw={2.2} />
          </button>
        )}
        <UpdateChip updatable={updatable} />
      </div>
      {icon && title !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 14px 6px' }}>
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 18,
              background: C.acc,
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={icon} size={22} />
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 21, lineHeight: 1.2, fontWeight: 700 }}>{title}</h1>
          </span>
          {step !== undefined && <StepDots step={step} />}
          {onMore && (
            <button
              type="button"
              aria-label={tr('gate.v1.a11y.moreOptions')}
              onClick={onMore}
              style={roundBtn(40)}
            >
              <Icon name="more" />
            </button>
          )}
        </div>
      )}
    </>
  )
}

type UpdateState = { kind: 'idle' } | { kind: 'busy' } | RefreshResult

/** The header's app version and update button (where मदद used to be). */
function UpdateChip({ updatable }: { updatable: boolean }) {
  useSafeToUpdate(updatable)
  const waiting = useAppUpdateWaiting()
  const { online } = useNetworkStatus()
  // Read softly: a screen drawn outside the app shell (tests, previews) has no auth.
  const status = useContext(AuthContext)?.status
  const sync = useSyncStatus()
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<UpdateState>({ kind: 'idle' })

  const run = async () => {
    if (state.kind === 'busy') return
    setState({ kind: 'busy' })
    setState(
      await refreshPhone({
        online,
        signedIn: status === 'authenticated',
        syncNow: sync.syncNow,
        downloadCaches: () => downloadCaches(),
        installAppUpdate: () => installAppUpdate(),
      }),
    )
  }

  const version = env.appVersion
  return (
    <>
      <button
        type="button"
        data-testid="update-chip"
        aria-label={tr('gate.v1.update.a11y', { version })}
        onClick={() => {
          setOpen(true)
          if (updatable) void run()
        }}
        style={{
          position: 'relative',
          height: 40,
          flexShrink: 0,
          padding: '0 12px 0 9px',
          borderRadius: 20,
          border: `1.5px solid ${C.border}`,
          background: C.surface,
          color: C.ink,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontFamily: FONT,
          cursor: 'pointer',
          boxSizing: 'border-box',
        }}
      >
        <Icon name="download" size={20} />
        <span style={{ fontSize: 14, fontWeight: 700 }}>v{version}</span>
        {waiting && (
          <span
            data-testid="update-dot"
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              width: 12,
              height: 12,
              borderRadius: 6,
              background: C.bad,
              border: `2px solid ${C.surface}`,
            }}
          />
        )}
      </button>
      {open && (
        <V1Sheet
          title={tr('gate.v1.update.title')}
          onClose={() => {
            if (state.kind === 'busy') return
            setOpen(false)
            setState({ kind: 'idle' })
          }}
          top={330}
          actions={
            updatable && state.kind !== 'busy' && state.kind !== 'idle' ? (
              <Cta label={tr('gate.v1.update.now')} icon="download" onClick={() => void run()} />
            ) : undefined
          }
        >
          <span style={{ fontSize: 16, color: C.muted }}>
            {tr('gate.v1.update.version', { version })}
          </span>
          {updatable ? (
            <UpdateMessage state={state} />
          ) : (
            <>
              <Strip icon="lock">{tr('gate.v1.update.locked')}</Strip>
              {waiting && <Strip icon="download">{tr('gate.v1.update.waiting')}</Strip>}
            </>
          )}
        </V1Sheet>
      )}
    </>
  )
}

function UpdateMessage({ state }: { state: UpdateState }) {
  switch (state.kind) {
    case 'idle':
      return <p style={{ margin: 0, fontSize: 17 }}>{tr('gate.v1.update.what')}</p>
    case 'busy':
      return (
        <Strip icon="hourglass">
          <span role="status">{tr('gate.v1.update.busy')}</span>
        </Strip>
      )
    case 'offline':
      return <Strip icon="bang">{tr('gate.v1.update.offline')}</Strip>
    case 'failed':
      return (
        <Strip icon="bang">
          {state.error instanceof DemoDeviceError
            ? tr('gate.v1.update.demo')
            : tr('gate.v1.update.failed', {
                error: state.error instanceof Error ? state.error.message : String(state.error),
              })}
        </Strip>
      )
    case 'signedOut':
      return (
        <Strip icon="bang">
          {state.app === 'reloading'
            ? tr('gate.v1.update.reloading')
            : tr('gate.v1.update.signedOut')}
        </Strip>
      )
    case 'updated':
      return (
        <Strip icon={state.app === 'reloading' ? 'hourglass' : 'ok'}>
          <span role="status">
            {state.app === 'reloading' ? tr('gate.v1.update.reloading') : tr('gate.v1.update.done')}
          </span>
        </Strip>
      )
  }
}

export function StepDots({ step, of = 6 }: { step: number; of?: number }) {
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 3,
        flexShrink: 0,
      }}
    >
      <b style={{ fontSize: 15, lineHeight: 1 }}>
        {step}/{of}
      </b>
      <span aria-hidden="true" style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        {Array.from({ length: of }, (_, i) => {
          const n = i + 1
          const cur = n === step
          const done = n < step
          return (
            <span
              key={n}
              style={{
                width: cur ? 10 : 7,
                height: cur ? 10 : 7,
                borderRadius: 6,
                background: cur ? C.acc : done ? C.ink : C.surface,
                border: `1.5px solid ${cur ? C.acc : done ? C.ink : C.border}`,
                boxSizing: 'border-box',
              }}
            />
          )
        })}
      </span>
    </span>
  )
}

/** The coloured line under the header: what is being tagged, or a paper slip. */
export function Band({
  tone,
  icon,
  children,
}: {
  tone: 'good' | 'red' | 'paper'
  icon: V1IconName
  children: ReactNode
}) {
  const t = {
    good: { background: C.okWash, color: C.ok },
    red: { background: C.bad, color: '#ffffff' },
    paper: { background: C.warnWash, color: C.accDark },
  }[tone]
  return (
    <div
      style={{
        ...t,
        padding: '5px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 16,
        fontWeight: 700,
      }}
    >
      <Icon name={icon} size={22} />
      <span>{children}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------------------------

/** The brown main action in the language on screen, with its icon after (or before) the words. */
export function Cta({
  label,
  icon,
  lead = false,
  onClick,
  busy,
  height = 60,
}: {
  label: string
  icon?: V1IconName
  /** Icon before the words (अगला किसान). */
  lead?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** Saving: the press is ignored. (Never disabled: a missing step shows WaitCta instead.) */
  busy?: boolean
  height?: number
}) {
  const sw = icon === 'next' ? 2.5 : 2.4
  const ic = icon ? <Icon name={icon} size={26} sw={sw} /> : null
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy || undefined}
      style={{
        height,
        borderRadius: 12,
        background: C.acc,
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        border: 0,
        fontFamily: FONT,
        width: '100%',
        padding: '0 14px',
        cursor: 'pointer',
        opacity: busy ? 0.75 : 1,
      }}
    >
      {lead && ic}
      <b style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.15 }}>{label}</b>
      {!lead && ic}
    </button>
  )
}

/** The main action while something is missing: dashed, with what is left. */
export function WaitCta({
  children,
  height = 60,
  onClick,
}: {
  children: ReactNode
  height?: number
  /** Optional: tapping takes her to what is missing (the look stays dashed). */
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      disabled={!onClick}
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        height,
        borderRadius: 12,
        background: C.sunk,
        color: C.muted,
        border: `1.5px dashed ${C.border}`,
        fontFamily: FONT,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontSize: 18,
        fontWeight: 700,
        boxSizing: 'border-box',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: 12,
          border: `2px solid ${C.border}`,
          color: C.border,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 700,
          boxSizing: 'border-box',
          lineHeight: 1,
        }}
      >
        ?
      </span>
      {children}
    </button>
  )
}

/** White outlined secondary action (footer rows, under scanners). */
export function Btn({
  icon,
  children,
  onClick,
  disabled,
  iconSize = 22,
}: {
  icon?: V1IconName
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  iconSize?: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        minHeight: 48,
        flex: '1 1 0',
        borderRadius: 10,
        background: C.surface,
        color: C.ink,
        border: `1.5px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        fontFamily: FONT,
        fontSize: 16,
        fontWeight: 700,
        padding: '0 10px',
        boxSizing: 'border-box',
        lineHeight: 1.15,
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {icon && <Icon name={icon} size={iconSize} />}
      <span>{children}</span>
    </button>
  )
}

export function Row({ children, gap = 8 }: { children: ReactNode; gap?: number }) {
  return <div style={{ display: 'flex', gap }}>{children}</div>
}

/** One answer of three (नहीं / पता नहीं / हाँ). */
export function Answer({
  children,
  pressed,
  onClick,
}: {
  children: ReactNode
  pressed: boolean
  onClick: () => void
}) {
  // Picked = the map's one selected look: 3px ink border and the corner tick.
  return (
    <Tile
      pressed={pressed}
      onClick={onClick}
      style={{
        minHeight: 56,
        borderRadius: 12,
        fontSize: 20,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        lineHeight: 1.15,
      }}
    >
      {children}
    </Tile>
  )
}

/** A picked option: 3px ink border and the black tick badge on its corner. */
export function Tile({
  pressed,
  onClick,
  children,
  style,
}: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        position: 'relative',
        border: pressed ? `3px solid ${C.ink}` : `1.5px solid ${C.border}`,
        background: C.surface,
        color: C.ink,
        fontFamily: FONT,
        boxSizing: 'border-box',
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
      {pressed && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 24,
            height: 24,
            borderRadius: 12,
            background: C.ink,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="ok" size={16} sw={3} />
        </span>
      )}
    </button>
  )
}

/** Good / Red tie, side by side (weigh screen). */
export function GradeTiles({
  red,
  onPick,
}: {
  red: boolean | null
  onPick: (red: boolean) => void
}) {
  const tile = (isRed: boolean) => (
    <Tile
      pressed={red === isRed}
      onClick={() => onPick(isRed)}
      style={{
        minHeight: 48,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '4px 8px',
        fontSize: 17,
        fontWeight: 700,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name={isRed ? 'redtie' : 'sack'} size={24} sw={1.8} />
      <span>{tr(isRed ? 'gate.v1.grade.red' : 'gate.v1.grade.good')}</span>
    </Tile>
  )
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
      {tile(false)}
      {tile(true)}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------------------------

/** White card, 1.5px rule border, 12px radius. */
export function Card({
  children,
  gap = 10,
  padding = '10px 12px',
  style,
}: {
  children: ReactNode
  gap?: number
  padding?: string
  style?: CSSProperties
}) {
  return (
    <section
      style={{
        background: C.surface,
        border: `1.5px solid ${C.rule}`,
        borderRadius: 12,
        padding,
        display: 'flex',
        flexDirection: 'column',
        gap,
        ...style,
      }}
    >
      {children}
    </section>
  )
}

/** Beige hint line with its icon (दो फ़सल हों तो एक-एक करके). */
export function Strip({
  icon,
  children,
  iconBox = false,
  iconSize = 22,
  onClick,
}: {
  icon: V1IconName
  children: ReactNode
  iconSize?: number
  /** Optional: the strip leads somewhere (shows a › at its end). */
  onClick?: () => void
  /** Icon in a white square (seal / close day strips). */
  iconBox?: boolean
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{
        border: 0,
        width: '100%',
        textAlign: 'left',
        fontFamily: FONT,
        cursor: onClick ? 'pointer' : undefined,
        display: 'flex',
        gap: iconBox ? 12 : 8,
        alignItems: 'center',
        padding: iconBox ? '8px 12px' : '8px 12px',
        borderRadius: 12,
        background: C.sunk,
        color: C.acc,
      }}
    >
      {iconBox ? (
        <span
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 10,
            background: C.surface,
            color: C.acc,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={icon} size={iconSize} />
        </span>
      ) : (
        <Icon name={icon} size={iconSize} />
      )}
      <span style={{ fontSize: 17, color: C.ink, lineHeight: iconBox ? 1.35 : 1.3 }}>
        {children}
      </span>
      {onClick && (
        <span style={{ marginLeft: 'auto', color: C.acc, display: 'flex' }}>
          <Icon name="next" size={22} sw={2.5} />
        </span>
      )}
    </Tag>
  )
}

/** Orange "cannot change after" note with the ! square. */
export function Warn({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        padding: '8px 12px',
        borderRadius: 12,
        background: C.warnWash,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 4,
          background: C.acc,
          color: '#ffffff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="bang" size={19} sw={3} />
      </span>
      <span style={{ fontSize: 16, lineHeight: 1.35, color: C.ink }}>{children}</span>
    </div>
  )
}

/** Green / red tick circle. */
export function Tick({ ok = true, size = 22 }: { ok?: boolean; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: size / 2,
        background: ok ? C.ok : C.bad,
        color: '#ffffff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name={ok ? 'ok' : 'x'} size={Math.round(size * 0.68)} sw={3} />
    </span>
  )
}

/** A job on a home screen: an icon, a bold line and a quieter one (आज's four jobs). */
export function JobTile({
  icon,
  color,
  title,
  line,
  onClick,
}: {
  /** A V1 icon, or any icon element (the phone home's feature icons). */
  icon: V1IconName | ReactNode
  color: string
  title: string
  line?: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        minHeight: 74,
        borderRadius: 12,
        background: C.surface,
        border: `1.5px solid ${C.border}`,
        color: C.ink,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        boxSizing: 'border-box',
        fontFamily: FONT,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span style={{ color, flexShrink: 0, display: 'flex' }}>
        {typeof icon === 'string' ? <Icon name={icon as V1IconName} size={30} /> : icon}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
        <b style={{ fontSize: 17 }}>{title}</b>
        {line && <span style={{ fontSize: 14, color: C.muted }}>{line}</span>}
      </span>
    </button>
  )
}

/** A row card: icon, bold line over a second line, something on the right. */
export function ItemRow({
  lead,
  title,
  line,
  right,
  border = C.rule,
  borderWidth = 1.5,
  background = C.surface,
  padding = '5px 10px',
  titleSize = 17,
  onClick,
}: {
  lead?: ReactNode
  title: ReactNode
  line?: ReactNode
  right?: ReactNode
  border?: string
  borderWidth?: number
  background?: string
  padding?: string
  titleSize?: number
  onClick?: () => void
}) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding,
        borderRadius: 12,
        background,
        border: `${borderWidth}px solid ${border}`,
        color: C.ink,
        boxSizing: 'border-box',
        fontFamily: FONT,
        textAlign: 'left',
        width: '100%',
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      {lead}
      <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: titleSize, fontWeight: 700, lineHeight: 1.25, color: C.ink }}>
          {title}
        </span>
        {line && <span style={{ fontSize: 16, color: C.ink, lineHeight: 1.3 }}>{line}</span>}
      </div>
      {right}
    </Tag>
  )
}

// ---------------------------------------------------------------------------------------------
// Typed numbers: readout + keypad
// ---------------------------------------------------------------------------------------------

/** The orange-edged box that shows what the keypad typed, with a caret. */
export function Readout({
  label,
  value,
  unit,
  side,
  speakText,
  size = 40,
  padding = '2px 10px 3px',
  labelSize = 15,
}: {
  label: string
  value: string
  unit?: string
  /** Right-hand figure (माल 49.28 किलो). */
  side?: ReactNode
  speakText?: string
  size?: number
  /** Moisture: '6px 12px 8px' with a 16px label. */
  padding?: string
  labelSize?: number
}) {
  return (
    <div
      style={{
        background: C.surface,
        border: `2px solid ${C.acc}`,
        borderRadius: 12,
        padding,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
      }}
    >
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: labelSize,
            color: C.muted,
            fontWeight: 600,
            lineHeight: labelSize > 15 ? 1.3 : 1.2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            minHeight: Math.ceil(size * 1.05),
          }}
        >
          <span style={{ fontSize: size, fontWeight: 700, lineHeight: 1.05 }}>{value}</span>
          <span
            aria-hidden="true"
            style={{
              width: 3,
              height: Math.round(size * 0.85),
              background: C.acc,
              marginLeft: 2,
              alignSelf: 'center',
            }}
          />
          {unit && <span style={{ fontSize: 20, fontWeight: 600, color: C.muted }}>{unit}</span>}
        </div>
      </div>
      {side}
      {speakText !== undefined && (
        <span style={{ alignSelf: 'center' }}>
          <SpeakButton text={speakText} size={44} label={tr('gate.v1.a11y.speak')} />
        </span>
      )}
    </div>
  )
}

const keyStyle: CSSProperties = {
  height: 50,
  borderRadius: 10,
  border: `1.5px solid ${C.border}`,
  background: C.surface,
  fontFamily: FONT,
  fontSize: 28,
  fontWeight: 600,
  color: C.ink,
  padding: 0,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}

/**
 * 3×4 keypad on the beige tray. A hardware keyboard works too (digits, '.', Backspace) while the
 * keypad is on screen.
 */
export function Keypad({
  onKey,
  dot = true,
}: {
  onKey: (k: string) => void
  /** Show '.' (weights, moisture); hidden for numbers and the PIN. */
  dot?: boolean
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return
      if (/^\d$/.test(e.key) || (dot && e.key === '.')) onKey(e.key)
      else if (e.key === 'Backspace') onKey('⌫')
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onKey, dot])
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', dot ? '.' : '', '0', '⌫']
  return (
    <div
      style={{
        background: C.sunk,
        padding: 5,
        borderRadius: 12,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 5,
      }}
    >
      {keys.map((k, i) =>
        k === '' ? (
          <span key={i} aria-hidden="true" />
        ) : (
          <button
            key={i}
            type="button"
            aria-label={k === '⌫' ? tr('pin.delete') : undefined}
            onClick={() => onKey(k)}
            style={keyStyle}
          >
            {k === '⌫' ? <Icon name="erase" size={28} /> : k}
          </button>
        ),
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Sheets and full-screen states
// ---------------------------------------------------------------------------------------------

/**
 * Bottom sheet as on the boards: the screen above dims to #57524d and a white panel with a grab
 * handle rises from the bottom, its two actions at the foot.
 */
export function V1Sheet({
  title,
  children,
  actions,
  onClose,
  top = 300,
}: {
  title?: string
  children: ReactNode
  actions?: ReactNode
  onClose: () => void
  /** Height of the dimmed band above the panel on a 720px screen (the boards vary 300–440). */
  top?: number
}) {
  const id = useId()
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])
  return createPortal(
    <div
      data-v1-screen
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        justifyContent: 'center',
        background: C.dim,
      }}
      data-v1-anim="backdrop"
    >
      <V1Sprite />
      <div
        style={{
          width: '100%',
          maxWidth: 640,
          height: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: FONT,
          color: C.ink,
          lineHeight: 1.35,
        }}
      >
        <button
          type="button"
          aria-label={tr('common.close')}
          onClick={onClose}
          style={{
            flex: `0 1 ${(top / 720) * 100}dvh`,
            minHeight: 40,
            background: C.dim,
            border: 0,
            cursor: 'pointer',
          }}
        />
        <section
          data-v1-anim="sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? id : undefined}
          style={{
            flexGrow: 1,
            marginTop: -20,
            background: C.surface,
            borderRadius: '20px 20px 0 0',
            padding: '10px 16px 14px',
            paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            overflowY: 'auto',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              alignSelf: 'center',
              width: 48,
              height: 5,
              borderRadius: 3,
              background: C.rule,
              flexShrink: 0,
            }}
          />
          {title && (
            <h1 id={id} style={{ margin: 0, fontSize: 22, lineHeight: 1.25 }}>
              {title}
            </h1>
          )}
          {children}
          <div style={{ flexGrow: 1 }} />
          {actions}
        </section>
      </div>
    </div>,
    document.body,
  )
}

/** Two sheet actions side by side (पीछे + the main one). */
export function SheetActions({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 8,
        alignItems: 'stretch',
      }}
    >
      {children}
    </div>
  )
}

/**
 * Dark scanner frame (tag and seal boards): the live camera fills it, with the white guide and
 * the words in the middle. The camera draws its own टॉर्च chip.
 */
export function ScannerFrame({
  icon,
  hint,
  children,
  minHeight = 190,
}: {
  icon: V1IconName
  hint: string
  /** The live camera. */
  children?: ReactNode
  /** Tag 190, seal 170. */
  minHeight?: number
}) {
  return (
    <div
      style={{
        flexGrow: 1,
        minHeight,
        position: 'relative',
        borderRadius: 14,
        background: C.scanner,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '22px 46px',
          border: '3px solid #ffffff',
          borderRadius: 12,
          opacity: 0.9,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          color: '#ffffff',
          textAlign: 'center',
          pointerEvents: 'none',
          textShadow: '0 1px 3px rgba(0,0,0,.5)',
        }}
      >
        <Icon name={icon} size={44} sw={1.6} />
        <b style={{ fontSize: 18 }}>{hint}</b>
      </div>
    </div>
  )
}

/** मदद on screens without their own picture help. */
export function GeneralHelpSheet({ onClose }: { onClose: () => void }) {
  return (
    <V1Sheet title={tr('gate.v1.help.generalTitle')} onClose={onClose} top={380}>
      <p style={{ margin: 0, fontSize: 18, lineHeight: 1.4 }}>{tr('gate.v1.help.general')}</p>
    </V1Sheet>
  )
}

/** The header clock: records and photos still on the phone, and अभी भेजें. */
function SyncSheet({ onClose }: { onClose: () => void }) {
  const sync = useSyncStatus()
  const done = sync.pending === 0 && sync.mediaPending === 0
  return (
    <V1Sheet
      title={tr('gate.v1.closeDay.toSend')}
      onClose={onClose}
      top={330}
      actions={
        <Cta
          label={tr('gate.v1.closeDay.sendNow')}
          icon="cloudup"
          busy={sync.phase === 'syncing'}
          onClick={() => void sync.syncNow()}
        />
      }
    >
      <ItemRow
        padding="8px 12px"
        lead={
          <Icon name={done ? 'ticks' : 'clock'} size={28} style={{ color: done ? C.ok : C.ink }} />
        }
        title={
          done
            ? tr('gate.v1.closeDay.allSentDone')
            : tr('gate.v1.closeDay.pending', { count: sync.pending, photos: sync.mediaPending })
        }
        line={
          sync.lastSyncedAt ? (
            <span style={{ color: C.muted }}>
              {tr('gate.v1.sync.last', { when: fmtWhen(sync.lastSyncedAt) })}
            </span>
          ) : undefined
        }
      />
      {sync.lastError && !done && (
        <div style={{ fontSize: 16, fontWeight: 700, color: C.bad }}>
          {tr('gate.v1.sync.failed')}
        </div>
      )}
      <p style={{ margin: 0, fontSize: 16, lineHeight: 1.4, color: C.muted }}>
        {tr('gate.v1.sync.offlineOk')}
      </p>
    </V1Sheet>
  )
}
